import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Select,
} from '@aeris/ui'

import { fetchCurrentCashSession } from '../lib/api/cash.ts'
import { ApiError } from '../lib/api/client.ts'
import { fetchPaymentMethods } from '../lib/api/payments.ts'
import { correctPaymentMethod } from '../lib/api/sales.ts'
import { useAuth } from '../lib/auth-context.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'

/**
 * Corrigir a forma de um pagamento — F319.
 *
 * O Eduardo: "coloquei uma parada como Pix e era dinheiro, aí dava pra mim
 * corrigir". A forma certa e o motivo aqui; a senha do gerente na janela de
 * autorização; o servidor estorna o errado e lança o certo com a mesma data.
 * Quando entra ou sai dinheiro da gaveta, vai no caixa aberto deste terminal.
 */
export const CorrectPaymentDialog = ({
  documentId,
  payment,
  onClose,
  onDone,
}: {
  documentId: string
  payment: {
    readonly id: string
    readonly paymentMethodId: string
    readonly methodName: string
    readonly amount: string
  }
  onClose: () => void
  onDone: (message: string) => void
}): ReactNode => {
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const [toMethodId, setToMethodId] = useState('')
  const [reason, setReason] = useState('')
  const [authorising, setAuthorising] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
  })
  const choices = (methods.data?.methods ?? []).filter(
    (method) =>
      !method.generatesReceivable &&
      method.kind !== 'voucher' &&
      method.id !== payment.paymentMethodId
  )

  const terminalId = readRememberedTerminal()
  const branchId = session?.context?.branchId ?? undefined
  const till = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: terminalId !== '',
  })

  const correct = useMutation({
    mutationFn: async (authorisationId: string) =>
      await correctPaymentMethod(documentId, payment.id, {
        toMethodId,
        reason: reason.trim(),
        authorisationId,
        cashSessionId: till.data?.session?.id ?? null,
      }),
    onSuccess: async () => {
      const to = choices.find((method) => method.id === toMethodId)?.name ?? ''
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['sales-document-payments', documentId],
        }),
        queryClient.invalidateQueries({ queryKey: ['sales-documents'] }),
        queryClient.invalidateQueries({ queryKey: ['cash-session'] }),
      ])
      onDone(`Pagamento corrigido: ${payment.methodName} → ${to}.`)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível corrigir o pagamento.'
      )
    },
  })

  const ready = toMethodId !== '' && reason.trim().length >= 3

  return (
    <>
      <Dialog
        open={!authorising}
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DialogContent
          title="Corrigir a forma de pagamento"
          description={`Hoje está como ${payment.methodName}. O valor e a data continuam os mesmos; só a forma muda, e fica no histórico com o motivo e quem autorizou.`}
        >
          <div className="flex flex-col gap-3">
            <Field label="Forma certa">
              {(field) => (
                <Select
                  {...field}
                  value={toMethodId}
                  onChange={(event) => {
                    setToMethodId(event.target.value)
                  }}
                >
                  <option value="">Escolha</option>
                  {choices.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Motivo" required>
              {(field) => (
                <Input
                  {...field}
                  value={reason}
                  placeholder="Ex.: era dinheiro, foi lançado como Pix"
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                />
              )}
            </Field>
            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button
              variant="primary"
              disabled={!ready || correct.isPending}
              onClick={() => {
                setError(null)
                setAuthorising(true)
              }}
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {authorising ? (
        <AuthorisationDialog
          title="Corrigir a forma de pagamento"
          description="O gerente, o admin ou o dono autoriza com a senha dele."
          action="sales.amend"
          targetType="sales_document"
          targetId={documentId}
          fixedReason={reason.trim()}
          onAuthorised={(authorisationId) => {
            setAuthorising(false)
            correct.mutate(authorisationId)
          }}
          onClose={() => {
            setAuthorising(false)
          }}
        />
      ) : null}
    </>
  )
}
